import { useIntl } from 'react-intl';

import {
    SidePanelBodyAttribute,
    SidePanelBodyAttributeBody,
    SidePanelBodyAttributeTitle,
} from 'src/components/sidePanel/SidePanelBody';
import { cardMessages } from 'src/features/cards/utils/messages';
import { statisticsMessages } from 'src/features/statistics/utils';
import { useParking } from 'src/providers/ParkingProvider';
import { Button } from 'src/registry/ui/Button';
import {
    DialogBody,
    DialogClose,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
} from 'src/registry/ui/Dialog';
import ImageModal from 'src/registry/ui/ImageModal';
import { Skeleton } from 'src/registry/ui/Skeleton';
import { getFormattedDateTimeForDetailPanel } from 'src/utils/getFormattedDateTime';
import { generalMessages } from 'src/utils/messages';

import type { StatisticManualPointOpens } from 'src/features/statistics/types';

type OperationManualPointOpensSidePanelProps = {
    manualPointOpens?: StatisticManualPointOpens;
};

type ManualPointsOpenTitleData = {
    title: string;
    value: string | null | undefined;
    control: any;
};
export function OperationManualPointOpensSidePanel(props: OperationManualPointOpensSidePanelProps) {
    const { manualPointOpens } = props;

    const { parkingId } = useParking();
    const { formatMessage } = useIntl();

    const sidePanelBodyAttributeMapping: ManualPointsOpenTitleData[] = [
        {
            title: formatMessage(cardMessages.cardTypeOperator),
            value: manualPointOpens?.operator_name,
            control: manualPointOpens?.operator_name,
        },
        {
            title: formatMessage(statisticsMessages.pointName),
            value: manualPointOpens?.point_name,
            control: manualPointOpens?.point_name,
        },
        {
            title: formatMessage(statisticsMessages.date),
            value: getFormattedDateTimeForDetailPanel(manualPointOpens?.event_datetime),
            control: manualPointOpens?.event_datetime,
        },
        {
            title: formatMessage(statisticsMessages.parkingZone),
            value: manualPointOpens?.parking_name,
            control: manualPointOpens?.parking_name,
        },
        {
            title: formatMessage(statisticsMessages.openReason),
            value: manualPointOpens?.open_reason,
            control: manualPointOpens?.open_reason,
        },
    ];

    const disableImageClick = !manualPointOpens?.photo_path;
    const imagePath = `/parkings/images/${parkingId}/${manualPointOpens?.photo_path}`;

    return (
        <>
            {manualPointOpens ? (
                <DialogHeader>
                    <div className="flex items-center gap-2">
                        <DialogTitle className="pr-0 font-mono">{manualPointOpens.parking_name}</DialogTitle>
                    </div>
                    <DialogDescription>
                        {manualPointOpens.event_datetime
                            ? getFormattedDateTimeForDetailPanel(manualPointOpens.event_datetime)
                            : ''}
                    </DialogDescription>
                </DialogHeader>
            ) : (
                <DialogHeader>
                    <Skeleton className="h-7 w-32" />
                    <Skeleton className="h-5 w-60" />
                </DialogHeader>
            )}
            <DialogSeparator />
            <DialogBody>
                <ImageModal
                    image={imagePath}
                    alt={manualPointOpens?.point_name}
                    title={manualPointOpens?.point_name}
                    disableClick={disableImageClick}
                />
                <div className="mt-4 grid gap-2">
                    {sidePanelBodyAttributeMapping.map((details) => {
                        return (
                            <SidePanelBodyAttribute key={details.title}>
                                <SidePanelBodyAttributeTitle>{details.title}</SidePanelBodyAttributeTitle>
                                <SidePanelBodyAttributeBody control={details.control}>
                                    {details.value}
                                </SidePanelBodyAttributeBody>
                            </SidePanelBodyAttribute>
                        );
                    })}
                </div>
            </DialogBody>
            <DialogSeparator />
            <DialogFooter className="flex flex-col gap-2 sm:flex-col">
                <DialogClose asChild>
                    <Button variant="outline" className="flex-1">
                        {formatMessage(generalMessages.close)}
                    </Button>
                </DialogClose>
            </DialogFooter>
        </>
    );
}
